import {
  Box,
  CssBaseline,
  Direction,
  PaletteMode,
  ThemeOptions,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import createCache, { StylisPlugin } from '@emotion/cache';
import { customShadows, shadows } from './shadows';

import { CacheProvider } from '@emotion/react';
import { Props } from '../@types';
import { breakpoints } from './breakpoints';
import componentsOverride from './overrides';
import { getDirection } from '@/lang/utils';
import { paletteGenerator } from './palette';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import shallow from 'zustand/shallow';
import { shape } from './shape';
import { typography } from './typography';
import { useRouter } from 'next/router';
import { useSettings } from '@/logic/state';

export interface CustomColor {
  0: string;
  10: string;
  20: string;
  30: string;
  40: string;
  50: string;
  60: string;
  70: string;
  80: string;
  90: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  500_8: string;
  500_12: string;
  500_16: string;
  500_24: string;
  500_32: string;
  500_48: string;
  500_56: string;
  500_80: string;
}

declare module '@mui/material' {
  interface Color {
    0: string;
    10: string;
    20: string;
    30: string;
    40: string;
    50: string;
    60: string;
    70: string;
    80: string;
    90: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    500_8: string;
    500_12: string;
    500_16: string;
    500_24: string;
    500_32: string;
    500_48: string;
    500_56: string;
    500_80: string;
  }
}
export interface CustomPalette {
  mode: PaletteMode;
  grey: CustomColor;
  layout: {
    backgroundColor: string;
    bgSecondary: string;
    bgPosterCard: string;
    posterCardInfoColor: string;
    posterDotColor: string;
  };
  backdrop: {
    blurBackgroundColor: string;
    dimmedBackgroundColor: string;
  };
  common: { black: string; white: string };
  primary: {
    main: string;
    light: string;
  };
  info: {
    main: string;
  };
  success: {
    main: string;
  };
  warning: {
    main: string;
  };
  error: {
    main: string;
  };
  secondary: {
    main: string;
  };
  link: {
    main: string;
  };
  supporting: {
    navy: string;
  };
  primaryLight: {
    main: string;
  };
  primaryMedium: {
    main: string;
  };
  darkGrey: {
    main: string;
  };
  lightGrey: {
    main: string;
  };

  toast: {
    error: string;
    success: string;
    info: string;
  };
  background: {
    default: string;
    paper: string;
  };
  tonalOffset: number;
  contrastThreshold: number;
  typography: {
    color: {
      muted: string;
      secondary: string;
    };
    font: {
      regular: string;
      light: string;
      bold: string;
    };
  };
  buttonAction: {
    active: string;
    hover: string;
    redHover: string;
  };
  orange: {
    main: string;
    light: string;
  };
  applePay: {
    primary: string;
    onHover: string;
  };
  action: {
    active: string;
    hover: string;
    customHover?: string;
    selected: string;
    disabled: string;
    disabledBackground: string;
    focus: string;
    hoverOpacity: number;
    disabledOpacity: number;
  };
}
declare module '@mui/material/styles' {
  interface Theme {
    customShadows: {
      z1: string;
      z8: string;
      z12: string;
      z16: string;
      z20: string;
      z24: string;
      primary: string;
      secondary: string;
      info: string;
      success: string;
      warning: string;
      error: string;
      darkGrey: string;
      cardShadow: string;
      footerShadow: string;
    };
  }
  interface Palette {
    primaryLight: {
      main: string;
    };
    primaryMedium: {
      main: string;
    };
    layout: {
      backgroundColor: string;
      bgSecondary: string;
      bgPosterCard: string;
      posterCardInfoColor: string;
      posterDotColor: string;
    };
    backdrop: {
      blurBackgroundColor: string;
      dimmedBackgroundColor: string;
    };
    lightGrey: {
      main: string;
    };
    darkGrey: { main: string };
    supporting: {
      navy: string;
    };
    toast: {
      error: string;
      success: string;
      info: string;
    };
    typography: {
      color: {
        muted: string;
        secondary: string;
      };
      font: {
        regular: string;
        light: string;
        bold: string;
      };
    };
    link: {
      main: string;
    };
    buttonAction: {
      active: string;
      hover: string;
      redHover: string;
    };
    orange: {
      main: string;
      light: string;
    };
    applePay: {
      primary: string;
      onHover: string;
    };
  }

  interface BreakpointOverrides {
    xs: false;
    sm: false;
    md: false;
    lg: false;
    xl: false;
    mobile: true;
    largeMobile: true;
    tablet: true;
    mediumTablet: true;
    largeTablet: true;
    laptop: true;
    desktop: true;
    mediumDesktop: true;
    largeDesktop: true;
    muviFixedContainer: true;
    extraLargeDesktop: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    secondary: true;
    darkGrey: true;
    lightGrey: true;
  }
}
declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    selected: true;
    'not-selected': true;
  }
}

declare module '@mui/material/Radio' {
  interface RadioPropsVariantOverrides {
    secondary: true;
  }
}

const dynamicPalette = {
  main: '#3C2C9f',
};

const getDynamicPalette = (): Promise<any> => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve(dynamicPalette);
    }, 2000);
  });
};

const ThemeConfig: FC<Props> = ({ children }) => {
  const themeMode = useSettings(s => s.themeMode, shallow);
  const [dynamicThemePalette, setDynamicPalette] = useState();

  const { locale } = useRouter();

  const direction: Direction = getDirection(locale);

  useEffect(() => {
    getDynamicPalette().then(val => {
      setDynamicPalette(val);
    });
  }, []);

  const themeOptions = useMemo<ThemeOptions>(
    () => ({
      palette: paletteGenerator(themeMode, dynamicThemePalette),
      shape,
      typography,
      shadows,
      customShadows,
      direction,
      breakpoints,
    }),
    [direction, themeMode, dynamicThemePalette]
  );

  const theme = createTheme(themeOptions);

  theme.components = componentsOverride(theme);

  const cacheRtl = createCache({
    key: 'muirtl',
    stylisPlugins: [prefixer, rtlPlugin as StylisPlugin],
  });

  return (
    <ThemeProvider theme={theme}>
      {direction === 'rtl' ? (
        <CacheProvider value={cacheRtl}>
          <CssBaseline />
          <Box
            sx={{
              height: '100%',
              // [theme.breakpoints.down('tablet')]: {
              //   width: 'fit-content',
              // },
            }}
          >
            {children}
          </Box>
        </CacheProvider>
      ) : (
        <>
          <CssBaseline />
          <Box
            sx={{
              height: '100%',
              // [theme.breakpoints.down('tablet')]: {
              //   width: 'fit-content',
              // },
            }}
          >
            {children}
          </Box>
        </>
      )}
    </ThemeProvider>
  );
};

export default ThemeConfig;
